import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import { Check, Crown } from "lucide-react";

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const navigate = useNavigate();

  const currencies = [
    { code: "USD", symbol: "$", name: "US Dollar" },
    { code: "INR", symbol: "₹", name: "Indian Rupee" },
    { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
  ];

  useEffect(() => {
    const loadData = async () => {
      await fetchPlans();
      await fetchCurrentSubscription();
    };
    loadData();
  }, [selectedCurrency]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPlans = async () => {
    try {
      const res = await api.get(
        `/subscriptions/plans?currency=${selectedCurrency}`
      );
      if (res.data.success) {
        setPlans(res.data.plans);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const res = await api.get("/subscriptions/current");
      if (res.data.success) {
        setCurrentSubscription(res.data.subscription);
      }
    } catch (error) {
      // User might not have a subscription yet
      console.log("No current subscription found");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setSubscribing(planId);
    try {
      let res;

      // Check if user has an active (non-expired) subscription
      const hasActiveSubscription =
        currentSubscription &&
        currentSubscription.status !== "expired" &&
        currentSubscription.approvalStatus === "approved" &&
        new Date(currentSubscription.currentPeriodEnd) > new Date();

      if (hasActiveSubscription) {
        // User has an active subscription, update it
        res = await api.put("/subscriptions/update", { planId });
        if (res.data.success) {
          alert(
            "Plan change request submitted successfully! Please wait for admin approval. Your current plan remains active until then."
          );
        }
      } else {
        // User doesn't have an active subscription (no subscription or expired), create new one
        res = await api.post("/subscriptions/create", { planId });
        if (res.data.success) {
          const message =
            currentSubscription && currentSubscription.status === "expired"
              ? "Subscription renewal request submitted successfully! Please wait for admin approval."
              : "Subscription request submitted successfully! Please wait for admin approval.";
          alert(message);
        }
      }

      if (res.data.success) {
        // Refresh the page to show pending requests
        window.location.reload();
      }
    } catch (error) {
      console.error("Error managing subscription:", error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert("Failed to manage subscription. Please try again.");
      }
    } finally {
      setSubscribing(null);
    }
  };

  const isCurrentPlan = (planId) => {
    return currentSubscription?.subscriptionPlanId === planId;
  };

  if (loading) {
    return (
      <LoadingSpinner
        fullScreen={true}
        size="lg"
        text="Loading subscription plans..."
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      {/* Header */}
      <div className="max-w-6xl mx-auto">
        <PageHeader
          showBackButton={true}
          title="Choose Your Plan"
          subtitle="Select the perfect plan for your business needs"
        />

        {/* Current Subscription Status */}
        {currentSubscription && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-blue-800">
              <Crown className="w-5 h-5" />
              <span className="font-medium">
                Current Plan: {currentSubscription.subscriptionPlan?.name}
              </span>
            </div>
            <p className="text-blue-600 text-sm mt-1">
              Valid until{" "}
              {new Date(
                currentSubscription.currentPeriodEnd
              ).toLocaleDateString()}
            </p>
          </div>
        )}

        {/* Currency Selector */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">
              Select Currency:
            </label>
            <select
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {currencies.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.symbol} {currency.name} ({currency.code})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`bg-white rounded-lg shadow-sm border-2 transition-all duration-300 ${
                isCurrentPlan(plan.id)
                  ? "border-blue-500 ring-2 ring-blue-100"
                  : "border-gray-200 hover:border-blue-300 hover:shadow-md"
              }`}
            >
              {/* Plan Header */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold text-gray-900">
                    {plan.name}
                  </h3>
                  {isCurrentPlan(plan.id) && (
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                      Current Plan
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-gray-900">
                    {
                      currencies.find((c) => c.code === selectedCurrency)
                        ?.symbol
                    }
                    {plan.price}
                  </span>
                  <span className="text-gray-500">/{plan.interval}</span>
                </div>
                <p className="text-gray-600 text-sm mt-2">{plan.description}</p>
              </div>

              {/* Features */}
              <div className="p-6 flex-1">
                <h4 className="font-semibold text-gray-900 mb-4">
                  What's included:
                </h4>
                <ul className="space-y-3">
                  {Object.entries(plan.features).map(([feature, included]) => (
                    <li key={feature} className="flex items-center gap-3">
                      <Check
                        className={`w-5 h-5 ${
                          included ? "text-green-500" : "text-gray-300"
                        }`}
                      />
                      <span
                        className={`text-sm ${
                          included ? "text-gray-700" : "text-gray-400"
                        }`}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Limits */}
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <h5 className="font-medium text-gray-900 mb-3">Limits:</h5>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div>
                      Customers:{" "}
                      {plan.maxCustomers === -1
                        ? "Unlimited"
                        : plan.maxCustomers}
                    </div>
                    <div>
                      Groups:{" "}
                      {plan.maxGroups === -1 ? "Unlimited" : plan.maxGroups}
                    </div>
                    <div>
                      Messages:{" "}
                      {plan.maxMessages === -1 ? "Unlimited" : plan.maxMessages}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="p-6 border-t border-gray-100">
                {isCurrentPlan(plan.id) ? (
                  <Button
                    variant="secondary"
                    size="lg"
                    className="w-full"
                    disabled={true}
                  >
                    Current Plan
                  </Button>
                ) : subscribing === plan.id ? (
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full"
                    disabled={true}
                  >
                    <LoadingSpinner
                      size="sm"
                      color="white"
                      text={
                        currentSubscription ? "Updating..." : "Subscribing..."
                      }
                    />
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full"
                    onClick={() => handleSubscribe(plan.id)}
                  >
                    {currentSubscription
                      ? "Change to This Plan"
                      : "Subscribe Now"}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
